import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { markOptimizationNotificationsRead } from "#modules/optimizations/infra/markOptimizationNotificationsRead.api";
import {
  getOptimizationsQueryData,
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";

export const useMarkOptimizationNotificationsRead = ({
  quoteRequestId,
}: {
  quoteRequestId: string;
}) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, ...rest } = useMutation({
    mutationFn: markOptimizationNotificationsRead,
    onMutate: async ({ optimizationId, notificationIds }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousOptimizations = getOptimizationsQueryData(queryClient, quoteRequestId);
      const readAt = new Date();

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) => {
          if (optimization.id !== optimizationId) {
            return optimization;
          }

          return {
            ...optimization,
            optimizationNotifications: optimization.optimizationNotifications.map((notification) =>
              notificationIds.includes(notification.id)
                ? { ...notification, readAt }
                : notification,
            ),
          };
        }),
      );

      return { previousOptimizations };
    },
    onError: (err: Error, _variables, context) => {
      if (context?.previousOptimizations) {
        setOptimizationsQueryData(queryClient, quoteRequestId, context.previousOptimizations);
      }

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
  });

  return { markOptimizationNotificationsRead: mutate, ...rest };
};
